import { useCallback, useEffect, useState } from 'react'
import { Loader2, ClipboardCheck, ClipboardList, ChevronRight, CheckCircle2, Clock } from 'lucide-react'
import { apiGet } from '../../lib/api.js'
import { nameInitial } from '../../lib/text.js'
import EvaluationRunner from '../../components/employee/EvaluationRunner.jsx'
import {
  fmtPercent, relationLabel, scoreTone, TONE_CLASS, relationShort,
} from '../../lib/evaluation.js'

// พื้นที่ของฉัน → ประเมิน. Two tabs, because an employee meets this feature wearing two
// different hats and conflating them is confusing:
//   ผลประเมินของฉัน   — my own score, and ONLY once HR has published it
//   แบบประเมินที่ต้องทำ — the questionnaires I owe about other people
//
// Everything here goes through /api/me/evaluation/*, which carries no employee id in any URL
// or body — the Xano stack resolves the caller from the session token. So there is nothing
// on this page that could be pointed at somebody else's record.

export default function MeEvaluation() {
  const [tab, setTab] = useState('mine')
  const [mine, setMine] = useState(null)
  const [tasks, setTasks] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [openTask, setOpenTask] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setErr('')
    try {
      const [m, t] = await Promise.all([
        apiGet('/api/me/evaluation'),
        apiGet('/api/me/evaluation/my-task'),
      ])
      setMine(m)
      setTasks(Array.isArray(t) ? t : [])
    } catch (e) {
      setErr(e?.message || 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const todo = tasks.filter((t) => t.status !== 'submitted')

  async function openOne(id) {
    setErr('')
    try {
      const full = await apiGet(`/api/me/evaluation/task/${id}`)
      setOpenTask(full)
    } catch (e) {
      setErr(e?.message || 'เปิดแบบประเมินไม่สำเร็จ')
    }
  }

  if (openTask) {
    return (
      <EvaluationRunner
        task={openTask}
        onBack={() => setOpenTask(null)}
        onSubmitted={() => { setOpenTask(null); load() }}
      />
    )
  }

  return (
    <div>
      {/* Tab switch */}
      <div className="flex items-center gap-1 mb-5">
        <TabBtn active={tab === 'mine'} onClick={() => setTab('mine')} icon={ClipboardCheck}>
          ผลประเมินของฉัน
        </TabBtn>
        <TabBtn active={tab === 'todo'} onClick={() => setTab('todo')} icon={ClipboardList} badge={todo.length}>
          แบบประเมินที่ต้องทำ
        </TabBtn>
      </div>

      {err && <div className="text-[13px] text-rose-500 mb-4">{err}</div>}

      {loading && (
        <div className="flex items-center gap-2 text-[13.5px] text-slate-400 py-10 justify-center">
          <Loader2 size={15} className="animate-spin" /> กำลังโหลด…
        </div>
      )}

      {!loading && tab === 'mine' && <MyResults data={mine} />}
      {!loading && tab === 'todo' && <MyTasks tasks={tasks} onOpen={openOne} />}
    </div>
  )
}

function TabBtn({ active, onClick, icon: Icon, badge, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-[13.5px] font-medium transition-colors ${
        active ? 'bg-brand-50 text-brand-700' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'
      }`}
    >
      <Icon size={14} strokeWidth={1.75} />
      {children}
      {badge > 0 && (
        <span className="ml-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[11px] font-semibold inline-flex items-center justify-center">
          {badge}
        </span>
      )}
    </button>
  )
}

function MyResults({ data }) {
  const results = data?.results || []

  if (!results.length) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-12 text-center">
        <p className="text-[14px] text-slate-500">ยังไม่มีผลประเมินที่เผยแพร่</p>
        <p className="text-[13px] text-slate-400 mt-1">
          ผลจะแสดงที่นี่เมื่อรอบประเมินเสร็จสิ้นและฝ่ายบุคคลเผยแพร่แล้ว
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {results.map((r) => (
        <div key={r.id} className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <div className="px-5 py-4 flex items-start justify-between gap-4 border-b border-slate-100">
            <div className="min-w-0">
              <h3 className="text-[15px] font-semibold text-slate-900">{r.cycle_title}</h3>
              <p className="text-[12.5px] text-slate-500 mt-0.5">
                {r.cycle_type}
                {r.period_start ? ` · ${r.period_start} ถึง ${r.period_end || '-'}` : ''}
              </p>
            </div>
            <div className="text-right flex-none">
              <div className="text-[26px] font-semibold text-slate-900 tabular-nums leading-none">
                {fmtPercent(r.total_percent)}
              </div>
              {r.decision && r.decision !== '-' && (
                <span className={`inline-flex mt-2 rounded-full px-2 py-0.5 text-[11.5px] font-medium ring-1 ${TONE_CLASS.violet}`}>
                  {r.decision}
                </span>
              )}
            </div>
          </div>

          <div className="px-5 py-4 space-y-2.5">
            {(r.group_scores || []).map((g) => (
              <div key={g.relation} className="flex items-center gap-3">
                <div className="w-28 flex-none text-[12.5px] text-slate-500">{relationLabel(g.relation)}</div>
                <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-brand-500"
                    style={{ width: `${Math.max(0, Math.min(100, Number(g.avg_percent || 0)))}%` }}
                  />
                </div>
                <div className="w-16 flex-none text-right text-[12.5px] tabular-nums text-slate-700">
                  {fmtPercent(g.avg_percent, 0)}
                </div>
                <div className="w-14 flex-none text-right text-[11.5px] text-slate-400">
                  {g.n_submitted}/{g.n_expected} คน
                </div>
              </div>
            ))}
            {/* Peer scores are shown as the group average only. Individual peer feedback is
                never attributed back to a named colleague on this screen. */}
          </div>
        </div>
      ))}
    </div>
  )
}

function MyTasks({ tasks, onOpen }) {
  if (!tasks.length) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-12 text-center">
        <p className="text-[14px] text-slate-500">ไม่มีแบบประเมินที่ต้องทำ</p>
        <p className="text-[13px] text-slate-400 mt-1">เมื่อมีรอบประเมินใหม่ จะแสดงที่นี่</p>
      </div>
    )
  }

  const todo = tasks.filter((t) => t.status !== 'submitted')
  const done = tasks.filter((t) => t.status === 'submitted')

  return (
    <div className="space-y-6">
      {todo.length > 0 && (
        <Group title="รอดำเนินการ" items={todo} onOpen={onOpen} />
      )}
      {done.length > 0 && (
        <Group title="ส่งแล้ว" items={done} onOpen={onOpen} muted />
      )}
    </div>
  )
}

function Group({ title, items, onOpen, muted }) {
  return (
    <div>
      <h3 className="text-[12.5px] font-medium text-slate-400 mb-2 px-1">{title}</h3>
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden divide-y divide-slate-50">
        {items.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => onOpen(t.id)}
            className="w-full flex items-center gap-3.5 px-5 py-3.5 text-left hover:bg-brand-50/40 transition-colors"
          >
            <div className="w-9 h-9 rounded-xl bg-slate-100 text-slate-500 flex items-center justify-center overflow-hidden flex-none ring-1 ring-slate-200/70">
              {t.subject?.image
                ? <img src={t.subject.image} alt="" className="w-full h-full object-cover" />
                : <span className="text-[13px] font-semibold">{nameInitial(t.subject?.nickname || t.subject?.name)}</span>}
            </div>

            <div className="min-w-0 flex-1">
              <div className={`text-[13.5px] font-medium truncate ${muted ? 'text-slate-500' : 'text-slate-900'}`}>
                {t.subject?.nickname || t.subject?.name || '—'}
                <span className="ml-2 text-[12px] font-normal text-slate-400">{t.subject?.position}</span>
              </div>
              <div className="text-[12px] text-slate-400 truncate">
                {t.cycle_title} · ในฐานะ{relationShort(t.relation)} · {t.n_questions} ข้อ
              </div>
            </div>

            {t.status === 'submitted' ? (
              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11.5px] font-medium ring-1 flex-none ${TONE_CLASS[scoreTone(t.score_percent)]}`}>
                <CheckCircle2 size={11} strokeWidth={2} /> ส่งแล้ว
              </span>
            ) : (
              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11.5px] font-medium ring-1 flex-none ${TONE_CLASS.amber}`}>
                <Clock size={11} strokeWidth={2} />
                {t.status === 'in_progress' ? 'ทำค้างไว้' : 'ยังไม่เริ่ม'}
              </span>
            )}

            <ChevronRight size={15} className="text-slate-300 flex-none" />
          </button>
        ))}
      </div>
    </div>
  )
}
